/**
 * Casino-local calendar dates for My Play ("Today", "Nov 1st", Last visit).
 *
 * My Play "Today" starts at midnight in the CASINO's local time zone, not UTC
 * and not the phone's zone. The zone comes from the casino record the app
 * already loads (casinoSchema from GetAllCasinos / `casinos` Mongo doc):
 *   1. casinoTimezoneNew  (existing field, IANA, e.g. "America/Los_Angeles")
 *   2. timezone           (optional IANA string)
 *   3. casinoTimezone     (legacy, e.g. "PST8PDT"; used only if valid)
 * Missing or invalid → DEFAULT_CASINO_TIME_ZONE (one default, not a casino list).
 * ms-casino resolves the same zone the same way for the jackpot cutoff.
 */

export const DEFAULT_CASINO_TIME_ZONE = 'America/Los_Angeles';

export type CasinoTimeZoneSource =
  | {
      casinoTimezoneNew?: unknown;
      timezone?: unknown;
      casinoTimezone?: unknown;
    }
  | null
  | undefined;

const validCache = new Map<string, boolean>();

export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string') return false;
  const s = tz.trim();
  if (!s) return false;
  const hit = validCache.get(s);
  if (hit !== undefined) return hit;
  let ok = false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: s }).format(0);
    ok = true;
  } catch {
    ok = false;
  }
  validCache.set(s, ok);
  return ok;
}

export function resolveCasinoTimeZone(casino: CasinoTimeZoneSource): string {
  if (casino && typeof casino === 'object') {
    for (const v of [casino.casinoTimezoneNew, casino.timezone, casino.casinoTimezone]) {
      if (isValidTimeZone(v)) return String(v).trim();
    }
  }
  return DEFAULT_CASINO_TIME_ZONE;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function ymdFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = fmtCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    fmtCache.set(timeZone, f);
  }
  return f;
}

/** Calendar date key YYYY-MM-DD for instant `d` in `timeZone`. */
export function localDateKey(d: Date, timeZone: string): string {
  const out: Record<string, string> = {};
  for (const p of ymdFormatter(timeZone).formatToParts(d)) {
    // eslint-disable-next-line security/detect-object-injection
    if (p.type !== 'literal') out[p.type] = p.value;
  }
  return `${out.year}-${out.month}-${out.day}`;
}

/** Today's casino-local date key. */
export function casinoTodayKey(timeZone: string, now: Date = new Date()): string {
  return localDateKey(now, timeZone);
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Date key for a My Play date value in the casino's zone.
 * - "YYYY-MM-DD" (playDate) is already a calendar day → used as-is.
 * - An instant at exactly 00:00:00.000Z is a calendar day stored as UTC
 *   midnight (current myPlay convention) → its UTC date, not shifted.
 * - Any other instant → its casino-local date.
 * Returns null when the value is missing or unparseable.
 */
export function casinoDateKey(value: unknown, timeZone: string): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) return value.trim();
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return null;
  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  ) {
    return d.toISOString().slice(0, 10);
  }
  return localDateKey(d, timeZone);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function ordinalDay(day: number): string {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return `${day}st`;
  if (j === 2 && k !== 12) return `${day}nd`;
  if (j === 3 && k !== 13) return `${day}rd`;
  return `${day}th`;
}

/** "2026-11-01" → "Nov 1st" (withYear: "Nov 1st 2026"). */
export function formatDateKey(key: string, withYear = false): string {
  const m = DATE_ONLY.exec(key);
  if (!m) return key;
  // eslint-disable-next-line security/detect-object-injection
  const month = MONTHS[Number(m[2]) - 1] || m[2];
  const label = `${month} ${ordinalDay(Number(m[3]))}`;
  return withYear ? `${label} ${m[1]}` : label;
}

/** Today tab header: casino-local today, e.g. "Nov 1st" at 8:47 PM PT Nov 1. */
export function formatCasinoTodayHeader(timeZone: string, now: Date = new Date()): string {
  return formatDateKey(casinoTodayKey(timeZone, now));
}

/**
 * Games Played group label: "Today" when the play date is casino-local today
 * (Today tab only), else "Nov 1st". Missing → "Today" on Today tab, "—" elsewhere.
 */
export function formatMyPlayDateLabel(
  playDate: unknown,
  period: string,
  timeZone: string,
  now: Date = new Date()
): string {
  if (playDate == null || playDate === '') return period === 'today' ? 'Today' : '—';
  const key = casinoDateKey(playDate, timeZone);
  if (!key) return String(playDate);
  if (period === 'today' && key === casinoTodayKey(timeZone, now)) return 'Today';
  return formatDateKey(key);
}

/** Last visit header / visits footer: "Nov 1st 2026" in casino-local terms. */
export function formatMyPlayVisitDate(value: unknown, timeZone: string): string {
  if (value == null || value === '') return '—';
  const key = casinoDateKey(value, timeZone);
  if (!key) return String(value);
  return formatDateKey(key, true);
}
